import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { useSelector } from 'react-redux';

import { Cell } from '@/App/BulkOrder/BulkOrderTables/Cell.tsx';
import { OrderStatus } from '@/App/BulkOrder/BulkOrderTables/OrderStatus.tsx';
import { WayCell } from '@/App/BulkOrder/BulkOrderTables/WayCell.tsx';
import { stopLossBulkOrderSelector } from '@/store/bulkOrder/selectors.ts';
import type { Trigger } from '@/store/bulkOrder/state.ts';

type OrderRowProps = {
  orderId: string;
  tabId: string;
};

export const StopLossOrderRow = (props: OrderRowProps) => {
  const { orderId, tabId } = props;
  const order = useSelector((state) => stopLossBulkOrderSelector(state, tabId, { orderId }));

  return (
    <tr data-e2e={`row-${order?.properties.currencyPair.replaceAll('/', '-')}-${order?.properties.bdrId}`}>
      <td width="120" align="left" data-e2e="status-cell">
        <OrderStatus status={order?.properties.status} errors={order?.properties.errors} />
      </td>

      <td width="140" align="left" className="text-success" data-e2e="status-oms-id-cell">
        <strong>{order?.properties.omsId}</strong>
      </td>

      <Cell width="120" field="bdrId" errors={order?.properties.errors} align="left">
        {order?.properties.bdrId}
      </Cell>

      <Cell width="120" field="currencyPair" errors={order?.properties.errors} align="left">
        {order?.properties.currencyPair}
      </Cell>

      <WayCell way={order?.properties.way} width="120" field="way" errors={order?.properties.errors} align="left">
        <FormattedMessage id={order?.properties.way === 'Ask' ? 'way.buy' : 'way.sell'} />
      </WayCell>

      <Cell width="200" field="notional" errors={order?.properties.errors} align="right">
        <FormattedNumber value={order?.properties.notional ?? 0} /> {order?.properties.currency}
      </Cell>

      <Cell width="120" field="limitPrice" errors={order?.properties.errors} align="right">
        <FormattedNumber value={order?.properties.limitPrice ?? 0} maximumFractionDigits={6} />
      </Cell>

      <Cell width="250" field="expiryDate" errors={order?.properties.errors} align="left">
        {order?.properties.expiry.type === 'ExpiryDate' ? (
          <FormattedDate
            value={order?.properties.expiry.dateTime}
            year="numeric"
            month="long"
            day="2-digit"
            hour="2-digit"
            minute="2-digit"
          />
        ) : (
          <FormattedMessage id="expirationDate.GTC" />
        )}
      </Cell>

      <Cell width="220" field="trigger" errors={order?.properties.errors} align="left">
        {order?.properties.trigger ? (
          <span className={`text-${getTriggerColor(order?.properties.trigger)}`}>
            <FormattedMessage id={`trigger.${order?.properties.trigger}`} />
          </span>
        ) : null}
      </Cell>
    </tr>
  );
};

const getTriggerColor = (trigger?: Trigger) =>
  trigger === 'Spot' ? 'azure' : trigger === 'BidOffer' ? 'primary' : 'secondary';
